import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { ERROR_NOTIFICATION_DISPLAY_DURATION, PATH_DEFINITIONS } from '@rappider/shared/definitions';
import { ContentEditorService, NotificationService, BrowserStorageManagementService, StringUtilityService } from '@rappider/services';
import { Action, Store } from '@ngrx/store';
import { ContentTree, ContentTreeItemType } from 'libs/content-tree-renderer/src/lib/models';
import {
  catchError,
  mergeMap,
  withLatestFrom,
  concatMap,
  map,
  expand,
  takeWhile
} from 'rxjs/operators';
import {
  ComponentControllerService,
  ComponentDefinitionControllerService,
  ComponentOutputEventControllerService,
  ContainerTemplateControllerService,
  CopyPageContentRequestDto,
  CopyPageContentTreeRequestDto,
  PageComponentControllerService,
  PageControllerService,
  PageWithRelations
} from '@rappider/rappider-sdk';

import { COMPONENT_GENERATION_IN_PROGRESS_STATUSES, DELAY_TO_CHECK_COMPONENT_GENERATION_STATUS } from './content-editor-helper-functions';


import * as ContentEditorActions from './content-editor.actions';
import * as AppActions from 'apps/rappider/src/app/state/app.actions';
import { activeContentTreeItemSelector } from '../selectors/active-content-tree-item.selector';
import { removeRelationalFieldsOfContentTree } from './content-editor-helper-functions';
import { Navigate } from '@rappider/shared';

import { v4 as uuidV4 } from 'uuid';
import { EMPTY, timer } from 'rxjs';
import * as LayoutActions from 'libs/pages/src/lib/state/layout-state/layout.actions';
import { CopyPage, SetActiveContentTreeItem, UpdateContentTreeOfPage } from './content-editor.actions';
import { ComponentGeneratorService } from '../services/component-generator.service';


@Injectable()
export class ContentEditorEffects {

  /* Saves page content reload count for an under construction container
   * so that we can stop loading the page content after a certain number of tries.
  */
  containerStatusVerifyCounter: Record<string, number> = {};

  constructor(
    private actions$: Actions,
    private pageApi: PageControllerService,
    private nzNotificationService: NzNotificationService,
    private contentEditorService: ContentEditorService,
    private notificationService: NotificationService,
    private store: Store<any>,
    private containerTemplateApi: ContainerTemplateControllerService,
    private browserStorageManagementService: BrowserStorageManagementService,
    private componentOutputEventApi: ComponentOutputEventControllerService,
    private pageComponentApi: PageComponentControllerService,
    private componentService: ComponentControllerService,
    private componentGeneratorService: ComponentGeneratorService
  ) { }


  getPageByIdWithIncludes$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.LoadPageWithComponents>(
      ContentEditorActions.ActionTypes.LoadPageWithComponents
    ),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId
      };
      return this.pageApi.getPageForContentEditor(params).pipe(
        concatMap((page: PageWithRelations) => [
          new ContentEditorActions.LoadPageWithComponentsSuccessful({ page })
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'LoadPageWithComponents',
            error: error,
          })
        ])
      );
    })
  ));

  createComponent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CreateComponent>
      (ContentEditorActions.ActionTypes.CreateComponent),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page),
      this.store.select(state => state.contentEditor?.page?.id),
      this.store.select(state => state.contentEditor?.page?.contentTree),
      this.store.select(<any>activeContentTreeItemSelector),
      this.store.select(state => state.contentEditor?.lastSavedContentTree),
    ),
    mergeMap(([action, activePage, activePageId, contentTree, activeContentTreeItem, lastSavedContentTree]) => {
      let pageContainerId: string;
      // Generate a new pageComponentId as uuid
      const pageComponentId = uuidV4();
      const newComponentId = uuidV4();

      /* if pageContainerId is null set pageContainerId as main container id*/
      if (activeContentTreeItem && activeContentTreeItem?.type === ContentTreeItemType.Container) {
        pageContainerId = activeContentTreeItem.id;
      } else {
        pageContainerId = contentTree[0].id;
      }

      /* Instead of waiting for the API response, we set an optimistic response */
      const optimisticComponentResponse = {
        id: pageComponentId,
        componentId: newComponentId,
        type: ContentTreeItemType.Component,
        component: {
          id: newComponentId,
          componentDefinitionId: action.payload.componentDefinitionId,
          title: action.payload.title || 'New Component',
          name: StringUtilityService.toPascalCase(action.payload.title) || 'new_component',
          cssClassName: StringUtilityService.toKebabCase(action.payload.title) || 'new-component',
          cssClasses: [],
          inputs: action.payload.inputs,
          outputs: {},
          isUpdatable: true,
          isDeleted: false,
          isDeletable: true,
        }
      };
      this.store.dispatch(new SetActiveContentTreeItem({ contentTreeItemId: pageComponentId }));
      // The time required to capture the necessary class for the function to operate
      setTimeout(() => {
        this.scrollToElementBySelector('.selected-component');
      }, 500);
      this.store.dispatch(
        new ContentEditorActions.CreateComponentSuccessful(
          { createdComponent: optimisticComponentResponse, pageContainerId: pageContainerId, pageComponentId: pageComponentId })
      );
      function generateUniqueString(array: string[], input: string): string {
        const regex = new RegExp(`^${input}( \\d+)?$`);
        let maxSuffix = 0;
        for (let value of array) {
          const match = value.match(regex);
          if (match) {
            const suffix = match[1];
            if (suffix) {
              const num = parseInt(suffix.trim(), 10);
              if (num > maxSuffix) {
                maxSuffix = num;
              }
            }
          }
        }
        return maxSuffix === 0 ? input : `${input} ${maxSuffix + 1}`;
      }
      const params = {
        pageId: activePageId,
        body: {
          pageContainerId: pageContainerId,
          componentDefinitionId: action.payload.componentDefinitionId,
          pageComponentId: pageComponentId,
          overridePageContainerComponent: {
            component: {
              id: newComponentId,
              title: generateUniqueString((activePage?.components || [])?.map(component => component.title), action.payload.title) || action.payload.title
            }
          },

        },
        options: {
          /* this flag prevents content tree update on a component creation  */
          updateContentTree: false
        }
      };
      const isContentTreesEqual = JSON.stringify(contentTree) === JSON.stringify(lastSavedContentTree);
      const addNewPageContainerComponentRequest = this.pageApi.addNewPageContainerComponent(params).pipe(
        mergeMap(() => {
          if (activePage.type === 'layout') {
            this.store.dispatch(new LayoutActions.GetLayoutById({ layoutId: activePageId }));
          }
          return EMPTY;
        }),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: 'CreateComponent', error: error })])
      );
      if (!isContentTreesEqual) {
        // eslint-disable-next-line max-len
        return this.pageApi.updateContentTreeByPageId({ pageId: activePageId, body: removeRelationalFieldsOfContentTree(contentTree) }).pipe(
          mergeMap(() => addNewPageContainerComponentRequest)
        );
      } else {
        return addNewPageContainerComponentRequest;
      }
    })
  ));

  updateComponentPosition$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CreateComponentSuccessful>
      (ContentEditorActions.ActionTypes.CreateComponentSuccessful),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.componentPosition)
    ),
    map(([action, componentPosition]) => {
      if (componentPosition?.component && componentPosition?.container && componentPosition?.index) {
        return new ContentEditorActions.MoveContentTreeItem({
          targetContentTreeItem: action.payload.createdComponent as any,
          targetPageContainer: componentPosition.container,
          targetIndex: componentPosition.index
        })
      } else {
        return { type: 'NO_ACTION' };
      }

    })
  ));

  /*
  When a component is generated with AI Assistant,
  - trigger AI component generate API
  - Save the description and image to display it later
    - image saved to project assets with metadata
    - description saved to container properties
  - If there is a container with `component-generation-in-progress` status; refresh the content tree every x second until a max try.
  - If there is a container with `component-generation-failure` client displays a message.
  - If there is a container with `component-generation-successful` or null status client displays the compiled component.

  */
  generateComponentWithAI$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.GenerateComponentIntoPageContainer>
      (ContentEditorActions.ActionTypes.GenerateComponentIntoPageContainer),
    mergeMap((action) => {
      const pageId = action.payload?.pageId;
      const pageContainerId = action.payload?.contentTreeItemId;
      const files = action.payload?.files || [];
      let description = action.payload?.description;

      if (files?.length && !description) {
        description += '\n\n Generate a component with the message and uploaded files.';
      }

      return this.componentGeneratorService.generateComponent(description, pageId, pageContainerId, files, 'angular').pipe(
        mergeMap((response: any) => {
          console.log('Generated component response:', response);
          response = response ? { ...response, componentDefinition: { ...response.componentDefinition, framework: 'angular' } } : {};

          return [
            new ContentEditorActions.GenerateComponentIntoPageContainerComponentGenerated({ response, pageId, pageContainerId })
          ];
        }
        ),
        catchError(error => {
          this.notificationService.createNotification(
            'error',
            error?.statusText,
            error?.error?.error?.message
          );
          return [new ContentEditorActions.ContentEditorError({ errorOn: 'GenerateComponentWithAI', error: error })]
        })
      );
    })
  ));

  /* this action gets all the containers in the page n seconds later and updates the page if the status is under construction */
  verifyStatusContainers$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.VerifyStatusOfAIGeneratedContainers>
      (ContentEditorActions.ActionTypes.VerifyStatusOfAIGeneratedContainers),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.contentTree)
    ),
    mergeMap(([action, contentTree]) => {

      const loadPageContentParams = {
        pageId: action.payload.pageId
      };

      // flat content tree items
      const flattenedContentTreeItems = this.componentGeneratorService.flattenChildrenOfTreeItem(contentTree);

      // find all container
      const pageContainersUnderConstruction = flattenedContentTreeItems.filter(item =>
        item.type === ContentTreeItemType.Container &&
        COMPONENT_GENERATION_IN_PROGRESS_STATUSES.includes(item.status)
      );

      if (pageContainersUnderConstruction?.length) {
        // If there are containers with component generation in progress, trigger this action again after a delay
        // try max 30 times
        const maxRetries = 30; // Maximum number of retries

        timer(DELAY_TO_CHECK_COMPONENT_GENERATION_STATUS).subscribe(() => {
          this.containerStatusVerifyCounter[action.payload.pageId] = (this.containerStatusVerifyCounter[action.payload.pageId] || 0) + 1;
          if (this.containerStatusVerifyCounter[action.payload.pageId] < maxRetries) {
            this.store.dispatch(new ContentEditorActions.LoadPageWithComponents(loadPageContentParams));
          }
        });
        return [{ type: '[ContentEditor] EMPTY' }];
      }
      return [{ type: '[ContentEditor] EMPTY' }];
    })));


  /* After API response is successful for component generation; we will verify if the bundle is compiled, every N seconds  */
  verifyStatusOfAIGeneratedComponentInContainer$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.GenerateComponentIntoPageContainerComponentGenerated>
      (ContentEditorActions.ActionTypes.GenerateComponentIntoPageContainerComponentGenerated),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.contentTree)
    ),
    mergeMap(([action, contentTree]) => {

      const loadPageContentParams = {
        pageId: action.payload.pageId
      };

      // find all container

      // If the componentDefinitionId is present, we will start checking the status of the compiled component
      if (action.payload.response?.componentDefinition?.id) {
        const maxRetries = 30; // Maximum number of retries

        return this.componentGeneratorService.getCompiledComponentStatus(action.payload.response?.componentDefinition?.id).pipe(
          expand((compiledComponentStatus, i) => {
            if (
              (compiledComponentStatus?.status === 'successful' ||
                compiledComponentStatus?.status === 'failure') ||
              i + 1 >= maxRetries
            ) {
              return EMPTY;
            }
            return timer(DELAY_TO_CHECK_COMPONENT_GENERATION_STATUS).pipe(
              mergeMap(() =>
                this.componentGeneratorService.getCompiledComponentStatus(action.payload.response?.componentDefinition?.id)
              )
            );
          }),
          takeWhile(
            (compiledComponentStatus, i) =>
              compiledComponentStatus?.status !== 'successful' &&
              compiledComponentStatus?.status !== 'failure' &&
              i + 1 < maxRetries,
            true
          ),
          mergeMap((compiledComponentStatus) => {
            if (compiledComponentStatus?.status === 'successful' || compiledComponentStatus.status === 'failure') {
              // component is compiled successfully, load the page with components
              return [
                new ContentEditorActions.LoadPageWithComponents(loadPageContentParams)
              ];
            } else if (
              compiledComponentStatus !== undefined &&
              compiledComponentStatus?.status !== 'successful' &&
              compiledComponentStatus?.status !== 'failure'
            ) {
              // If not successful after max retries, you can dispatch an error or a failure action
              return [
                new ContentEditorActions.ContentEditorError({
                  errorOn: 'GenerateComponentWithAI',
                  error: 'Component generation did not succeed after maximum retries.'
                })
              ];
            }
            return [];
          }),
          catchError((error) => [
            new ContentEditorActions.ContentEditorError({
              errorOn: 'LoadPageWithComponents',
              error: error,
            })
          ])
        );
      } else {
        return EMPTY;
      }
    }))
  );

  reloadPageContent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.ReloadPageContent>(
      ContentEditorActions.ActionTypes.ReloadPageContent
    ),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId
      };
      return this.pageApi.getPageForContentEditor(params).pipe(
        concatMap((page: PageWithRelations) => [
          new ContentEditorActions.LoadPageWithComponentsSuccessful({ page })
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'LoadPageWithComponents',
            error: error,
          })
        ])
      );
    })
  ));

  /* trigger verifyStatusOfAIGeneratedComponentInContainer on LoadPageWithComponentsSuccessful
    */
  checkIfThereIsAIGeneratedComponentInContainer$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.LoadPageWithComponentsSuccessful>
      (ContentEditorActions.ActionTypes.LoadPageWithComponentsSuccessful),
    mergeMap((action) => [
      new ContentEditorActions.VerifyStatusOfAIGeneratedContainers({ pageId: action.payload.page?.id })
    ])
  ));

  /*
  When a component is generated and added into a container; ContentEditorActions.GenerateComponentIntoPageContainerSuccessful
      - If there is a container with `component-generation-in-progress` status; refresh the content tree every x second until a max try.
    - If there is a container with `component-generation-failure` client displays a message.
    - If there is a container with `component-generation-successful` or null status client displays the compiled component.

  */
  checkStatusOfAIGeneratedComponentInContainer$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.GenerateComponentIntoPageContainerSuccessful>
      (ContentEditorActions.ActionTypes.GenerateComponentIntoPageContainerSuccessful),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.components)
    ),
    mergeMap(([action, components]) => {

      const aiGeneratedComponentDefinition = action.payload?.response?.componentDefinition;
      if (aiGeneratedComponentDefinition && components) {
        const aiGeneratedComponent = components.find(
          (c: { componentDefinitionId: unknown }) =>
            c.componentDefinitionId === aiGeneratedComponentDefinition.id
        );
        if (aiGeneratedComponent) {
          this.store.dispatch(
            new ContentEditorActions.SetActiveContentTreeItem({
              contentTreeItemId: aiGeneratedComponent.id
            })
          );
          this.scrollToElementBySelector('.selected-component');
        }
      }
      // If the component is generated successfully, we can return a no-op action
      return [{ type: 'Empty' }];
    })
  ));

  deleteComponent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.DeleteComponent>
      (ContentEditorActions.ActionTypes.DeleteComponent),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.id)
    ),
    mergeMap(([action, activePageId]) => {
      const params = {
        pageId: activePageId,
        body: {
          pageContainerComponentId: action.payload.componentId
        }
      };
      return this.pageApi.deletePageContainerComponent(params).pipe(
        mergeMap((response: ContentTree) => [
          new ContentEditorActions.DeleteComponentSuccessful({ deletedComponentId: action.payload.componentId })
        ]),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: 'DeleteComponent', error: error })])
      );
    })
  ));


  updateComponent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateComponent>(
      ContentEditorActions.ActionTypes.UpdateComponent
    ),
    withLatestFrom(
      this.store.select((state) => state.contentEditor?.page?.id)
    ),
    mergeMap(([action, pageId]) => {
      const component = action.payload.component;
      return this.componentService.updateById({ id: component.id, body: component }).pipe(
        // fix
        mergeMap((res) => ([new ContentEditorActions.UpdateComponentSuccessful({ component: res })])),
        catchError((error) => {
          this.notificationService.createNotification(
            'error',
            'SHARED.ERROR',
            'CONTENT_EDITOR_MODULE.COMPONENT_SETTINGS_COMPONENT.ERROR_ON_UPDATING_COMPONENT',
            { nzDuration: ERROR_NOTIFICATION_DISPLAY_DURATION }
          );
          return [
            new ContentEditorActions.ContentEditorError({
              errorOn: 'UpdateComponent',
              error: error,
            }),
          ];
        })
      );
    })
  ));

  updateComponentInputs$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateComponentInputs>(
      ContentEditorActions.ActionTypes.UpdateComponentInputs
    ),
    withLatestFrom(
      this.store.select((state) => state.contentEditor?.page?.components),
      this.store.select((state) => state.contentEditor?.page?.id)
    ),
    mergeMap(([action, components, pageId]) => {
      const component = components.find(c => c.id === action.payload.componentId);
      const inputs = {
        ...(component?.inputs ?? []),
        ...action.payload.inputs
      };
      return this.componentService.updateInputs({ id: action.payload.componentId, body: inputs }).pipe(
        mergeMap(() => {
          if (action.payload.pageType && action.payload.pageType === 'layout') {
            this.store.dispatch(new LayoutActions.GetLayoutById({ layoutId: pageId }));
          }
          return [];
        }),
        catchError((error) => {
          this.nzNotificationService.error(
            'Error',
            'There was an error while updating the input.',
            { nzDuration: ERROR_NOTIFICATION_DISPLAY_DURATION }
          );
          return [
            new ContentEditorActions.ContentEditorError({
              errorOn: 'UpdateComponentInputs',
              error: error,
            })
          ];
        })
      );
    })
  ));


  updatePageCss$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdatePageCss>
      (ContentEditorActions.ActionTypes.UpdatePageCss),
    withLatestFrom(
      this.store.select((state) => state.contentEditor?.page)
    ),
    mergeMap(([action, page]) => {
      const params = {
        id: action.payload.pageId,
        body: {
          cssStyle: action.payload.css
        }
      };
      return this.pageApi.updateById(params).pipe(
        mergeMap(() => {
          this.notificationService.createNotification('success', 'SHARED.SUCCESSFUL', 'Page style updated successfully');
          if (page.type === 'layout') {
            return [
              new ContentEditorActions.UpdatePageCssSuccesful({ pageId: action.payload.pageId, css: action.payload.css }),
              new LayoutActions.GetLayoutById({ layoutId: params.id })
            ];
          } else {
            return [
              new ContentEditorActions.UpdatePageCssSuccesful({ pageId: action.payload.pageId, css: action.payload.css })
            ];
          }
        }),
        catchError(error => {
          this.notificationService.createNotification('error', 'SHARED.ERROR', 'Page style could not updated');
          return [
            new ContentEditorActions.ContentEditorError({ errorOn: 'UpdatePageCss', error: error })
          ];
        })
      );
    })
  ));


  copyPage$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CopyPage>
      (ContentEditorActions.ActionTypes.CopyPage),
    mergeMap(action => {
      const params: CopyPageContentRequestDto = {
        sourcePageId: action.payload.sourcePageId,
        targetPageId: action.payload.targetPageId
      };
      return this.pageApi.copyPageContent({ body: params }).pipe(
        mergeMap(() => ([
          new ContentEditorActions.LoadPageWithComponents({ pageId: action.payload.targetPageId }),
        ])),
        catchError(error => ([
          new ContentEditorActions.ContentEditorError({ errorOn: 'CopyPage', error: error })
        ]))
      );
    })
  ));

  copyContainer$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CopyContainer>
      (ContentEditorActions.ActionTypes.CopyContainer),
    mergeMap(action => {
      const params: CopyPageContentRequestDto = {
        sourcePageId: action.payload.sourcePageId,
        targetPageId: action.payload.targetPageId,
        componentPairs: action.payload.componentPairs,
        targetContainerId: action.payload.targetContainerId,
      };
      return this.pageApi.copyPageContent({ body: params }).pipe(
        map(() => new ContentEditorActions.CopyContainerSuccessful()
        ),
        catchError(error => ([
          new ContentEditorActions.ContentEditorError({ errorOn: 'CopyPage', error: error })
        ]))
      );
    })
  ));


  copyTemplateToPageContainer$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CopyTemplateToPageContainer>
      (ContentEditorActions.ActionTypes.CopyTemplateToPageContainer),
    mergeMap((action) => {
      const params = { body: { containerTemplateTree: action.payload.templateTree, pageContainerId: action.payload.targetContainerId, pageId: action.payload.targetPageId } };
      return this.containerTemplateApi.copyTemplateToPageContainer(params).pipe(
        mergeMap(contentTree => [
          new ContentEditorActions.CopyTemplateToPageContainerSuccessful({ contentTree: contentTree }),
          new ContentEditorActions.LoadPageWithComponents({ pageId: action.payload.targetPageId })]
        ),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: ContentEditorActions.ActionTypes.CopyTemplateToPageContainer, error: error })])
      );
    })
  ));

  copyPageContentTree$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CopyPageContentTree>
      (ContentEditorActions.ActionTypes.CopyPageContentTree),
    mergeMap((action) => {
      const params: CopyPageContentTreeRequestDto = {
        targetPageId: action.payload.targetPageId,
        contentTree: action.payload.contentTree,
      };

      return this.pageApi.copyPageContentTree({ body: params }).pipe(
        mergeMap((contentTree) => {
          const actions: any[] = [
            new ContentEditorActions.CopyPageContentTreeSuccessful({ contentTree: contentTree })
          ];

          if (action.payload.targetContainerId) {
            actions.push(new ContentEditorActions.LoadPageWithComponents({ pageId: action.payload.targetPageId }));
          }

          return actions;
        }),
        catchError(error => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'CopyPageContentTree',
            error: error
          })
        ])
      );
    })
  ));


  copyActiveContentTemplateToLocalStorage$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CopyActiveContentTemplateToLocalStorage>
      (ContentEditorActions.ActionTypes.CopyActiveContentTemplateToLocalStorage),
    withLatestFrom(
      this.store.select(<any>activeContentTreeItemSelector),
      this.store.select(state => state.contentEditor?.page),
    ),
    mergeMap(([action, activeContentTreeItem, page]) => {
      if (activeContentTreeItem?.type === ContentTreeItemType.Container) {
        function extractChildrenIds(obj) {
          let resultStringArray = [];
          function traverse(current) {
            if (Array.isArray(current)) {
              current.forEach(traverse);
            } else if (typeof current === 'object' && current !== null) {
              for (const key in current) {
                if (key === 'componentId') {
                  resultStringArray.push(current[key]);
                } else {
                  traverse(current[key]);
                }
              }
            }
          }
          traverse(obj);
          return resultStringArray;
        }
        const childrenIds = extractChildrenIds(activeContentTreeItem);
        const clipboardData = {
          components: page.components?.filter(component => childrenIds.includes(component.id)),
          ...activeContentTreeItem
        }
        this.browserStorageManagementService.setLocalStorageItem('contentEditorClipboard', JSON.stringify(clipboardData));
        this.notificationService.createNotification('success', activeContentTreeItem.title, 'Template copied into clipboard');
        return [new ContentEditorActions.WriteCopiedTemplateTreeFromLocalStorageToState(clipboardData)];
      } else {
        return [new ContentEditorActions.WriteCopiedTemplateTreeFromLocalStorageToStateFailure({ error: 'activeContentTreeItem type could not match to container', key: 'WriteCopiedTemplateTreeFromLocalStorageToStateFailure', timestamp: Date.now() })];
      }
    })
  ));


  writeCopiedTemplateTreeFromLocalStorageToState$ = createEffect(() => this.actions$.pipe(
    ofType<AppActions.LoadApp>(AppActions.ActionTypes.LoadApp),
    mergeMap(() => {
      const copiedTemplate = this.browserStorageManagementService.getLocalStorageItem('contentEditorClipboard');
      if (copiedTemplate) {
        const parsedData = JSON.parse(copiedTemplate);
        return [new ContentEditorActions.WriteCopiedTemplateTreeFromLocalStorageToState(parsedData)];
      }
      return [new AppActions.LoadAppSuccessful()];
    })
  ));


  pasteTemplateFromLocalStorage$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.PasteTemplateFromLocalStorage>
      (ContentEditorActions.ActionTypes.PasteTemplateFromLocalStorage),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.activeContentTreeItemId),
      this.store.select(state => state.contentEditor?.page?.id),
    ),
    mergeMap(([action, activeContentTreeItemId, pageId]) => {
      const actionsToDispatch = [];
      const clipboardData = this.browserStorageManagementService.getLocalStorageItem('contentEditorClipboard');
      if (clipboardData) {
        const parsedClipboardData = JSON.parse(clipboardData);
        const templateTree = parsedClipboardData?.templateTree;
        const data = {
          templateTree: templateTree,
          targetContainerId: activeContentTreeItemId,
          targetPageId: pageId
        };
        actionsToDispatch.push(new ContentEditorActions.CopyTemplateToPageContainer(data));
      }
      return actionsToDispatch;
    })
  ));


  createContainerVisibilityCondition$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CreateContainerVisibilityCondition>
      (ContentEditorActions.ActionTypes.CreateContainerVisibilityCondition),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId,
        body: action.payload.body
      };
      return this.pageApi.addConditionToContainer(params).pipe(
        mergeMap(response => ([
          new ContentEditorActions.CreateContainerVisibilityConditionSuccessful({
            pageContainerId: action.payload.body.pageContainerId,
            createdVisibilityCondition: response
          })
        ])),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: 'CreateContainerVisibilityCondition', error: error })])
      );
    })
  ));


  updateContainerVisibilityCondition$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateContainerVisibilityCondition>
      (ContentEditorActions.ActionTypes.UpdateContainerVisibilityCondition),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId,
        body: action.payload.body
      };
      return this.pageApi.updateContainerCondition(params).pipe(
        mergeMap(response => ([
          new ContentEditorActions.UpdateContainerVisibilityConditionSuccessful({
            pageContainerId: action.payload.body.pageContainerId,
            updatedVisibilityCondition: response
          })
        ])),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: 'UpdateContainerVisibilityCondition', error: error })])
      );
    })
  ));


  deleteContainerVisibilityCondition$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.DeleteContainerVisibilityCondition>
      (ContentEditorActions.ActionTypes.DeleteContainerVisibilityCondition),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId,
        body: {
          pageContainerId: action.payload.pageContainerId
        }
      };
      return this.pageApi.removeContainerCondition(params).pipe(
        mergeMap(response => ([
          new ContentEditorActions.DeleteContainerVisibilityConditionSuccessful({
            pageContainerId: action.payload.pageContainerId
          })
        ])),
        catchError(error => [new ContentEditorActions.ContentEditorError({ errorOn: 'DeleteContainerVisibilityCondition', error: error })])
      );
    })
  ));


  updateContentTreeOfPage$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateContentTreeOfPage>
      (ContentEditorActions.ActionTypes.UpdateContentTreeOfPage),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.id),
      this.store.select(state => state.contentEditor?.page?.contentTree),
      this.store.select(state => state.contentEditor?.componentIdsToRemove),
      this.store.select(state => state.layout?.data),
    ),
    mergeMap(([action, pageId, contentTree, componentIdsToRemove, layouts]) => {
      if (pageId != null && contentTree != null) {
        const pureTree = removeRelationalFieldsOfContentTree(contentTree);
        const params = {
          pageId: pageId,
          body: contentTree
        };
        return this.pageApi.updateContentTreeByPageId(params).pipe(
          mergeMap(() => {
            const actionsToDispatch: Action[] = [
              new ContentEditorActions.UpdateContentTreeOfPageSuccessful({ contentTree: pureTree }),
            ];
            const isLayout = layouts.find(layout => layout.id === pageId);
            if (isLayout) {
              actionsToDispatch.push(
                new LayoutActions.UpdateContentTreeOfLayout({ layoutId: pageId, contentTree: pureTree })
              )
            }
            if (componentIdsToRemove?.length) {
              actionsToDispatch.push(
                new ContentEditorActions.DeletePageComponents({ componentIds: componentIdsToRemove, pageId: pageId })
              );
            }
            return actionsToDispatch;
          }),
          catchError(e => [new ContentEditorActions.ContentEditorError({ error: e, errorOn: 'UpdateContentTreeOfPage' })])
        );
      } else {
        return [];
      }
    })
  ));

  updateContentTreeAsIs$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateContentTreeAsIs>
      (ContentEditorActions.ActionTypes.UpdateContentTreeAsIs),
    mergeMap((action) => {
      if (action.payload?.contentTree != null) {
        const pureContentTree = removeRelationalFieldsOfContentTree(action.payload.contentTree);
        const params = {
          pageId: action.payload.pageId,
          body: pureContentTree
        };
        return this.pageApi.updateContentTreeByPageId(params).pipe(
          mergeMap(() => {
            const actionsToDispatch: Action[] = [
              new ContentEditorActions.UpdateContentTreeAsIsSuccessful({ contentTree: pureContentTree }),
            ];
            return actionsToDispatch;
          }),
          catchError(e => [new ContentEditorActions.ContentEditorError({ error: e, errorOn: 'UpdateContentTreeAsIs' })])
        );
      } else {
        return [];
      }
    })
  ));


  contentEditorDestroy$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.ContentEditorDestroy>
      (ContentEditorActions.ActionTypes.ContentEditorDestroy),
    mergeMap((action) => {
      const actions: Action[] = [
        new ContentEditorActions.ResetContentEditorData()
      ];
      // If targetPageId exists, the page change in the content editor is done using navigate.
      if (action.payload?.targetPageId) {
        actions.push(new Navigate({ url: `${PATH_DEFINITIONS.CONTENT_EDITOR.CONTENT_EDITOR_PATH}/${action.payload?.targetPageId}` }));
      }
      return actions;
    })
  ));


  createComponentOutputEvent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.CreateComponentOutputEvent>
      (ContentEditorActions.ActionTypes.CreateComponentOutputEvent),
    mergeMap((action) => {
      const params = {
        body: action.payload.createdOutputEvent
      };
      return this.componentOutputEventApi.create(params).pipe(
        mergeMap((createdComponentOutputEvent) => [
          new ContentEditorActions.CreateComponentOutputEventSuccessful({
            createdOutputEvent: createdComponentOutputEvent,
            componentId: action.payload.componentId
          })
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'CreateComponentOutputEvent',
            error: error
          })
        ])
      );
    })
  ));


  updateComponentOutputEvent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.UpdateComponentOutputEvent>
      (ContentEditorActions.ActionTypes.UpdateComponentOutputEvent),
    mergeMap((action) => {
      const params = {
        id: action.payload.updatedOutputEventId,
        body: action.payload.updatedOutputEvent
      };
      return this.componentOutputEventApi.updateById(params).pipe(
        mergeMap((_) => [
          new ContentEditorActions.UpdateComponentOutputEventSuccessful(action.payload)
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'UpdateComponentOutputEvent',
            error: error
          })
        ])
      );
    })
  ));


  deleteComponentOutputEvent$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.DeleteComponentOutputEvent>
      (ContentEditorActions.ActionTypes.DeleteComponentOutputEvent),
    mergeMap((action) => {
      const params = {
        id: action.payload.eventId,
      };
      return this.componentOutputEventApi.deleteById(params).pipe(
        mergeMap((_) => [
          new ContentEditorActions.DeleteComponentOutputEventSuccessful(action.payload)
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'DeleteComponentOutputEvent',
            error: error
          })
        ])
      );
    })
  ));


  deletePageComponents$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.DeletePageComponents>
      (ContentEditorActions.ActionTypes.DeletePageComponents),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId,
        componentIds: action.payload.componentIds
      };
      return this.pageComponentApi.removeComponentsFromPage(params).pipe(
        mergeMap((_) => [
          new ContentEditorActions.DeletePageComponentsSuccessful({ pageId: action.payload.pageId, removedComponentIds: action.payload.componentIds })
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'DeletePageComponents',
            error: error
          })
        ])
      );
    })
  ));

  getTemplateById$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.GetTemplateById>
      (ContentEditorActions.ActionTypes.GetTemplateById),
    mergeMap((action) => {
      const params = {
        id: action.payload.id,
        filter: {
          include: [
            {
              relation: 'components',
            }
          ],
        }
      };
      return this.pageApi.findById(params).pipe(
        map((template) => new ContentEditorActions.GetTemplateByIdSuccessful({ template })
        ),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'GetTemplateById',
            error: error
          })
        ])
      );
    })
  ));

  generateSubscriptionsAndValuesByAi$ = createEffect(() => this.actions$.pipe(
    ofType<ContentEditorActions.GenerateSubscriptionsAndValuesByAi>
      (ContentEditorActions.ActionTypes.GenerateSubscriptionsAndValuesByAi),
    mergeMap((action) => {
      const params = {
        pageId: action.payload.pageId
      };
      return this.pageApi.generateSubscriptionsAndComponentInputValueByAI(params).pipe(
        mergeMap(() => [
          new ContentEditorActions.GenerateSubscriptionsAndValuesByAiSuccessful({ pageId: action.payload.pageId })
        ]),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'GenerateSubscriptionsAndValuesByAi',
            error: error
          })
        ])
      );
    })
  ));

  updateContentTreeByCopy$ = createEffect(() => this.actions$.pipe(
    ofType(ContentEditorActions.ActionTypes.CreateComponent, ContentEditorActions.ActionTypes.GetTemplateByIdSuccessful, ContentEditorActions.ActionTypes.DeletePageContainer, ContentEditorActions.ActionTypes.DeleteComponentSuccessful),
    map((action) => new UpdateContentTreeOfPage())
  ));

  getPageByIdAndReplace$ = createEffect(() => this.actions$.pipe(
    ofType
      (
        ContentEditorActions.ActionTypes.UpdateContentTreeOfPageSuccessful
      ),
    withLatestFrom(
      this.store.select(state => state.contentEditor?.page?.id)
    ),
    mergeMap(([action, pageId]) => {
      const params = {
        id: pageId,
        filter: {
          include: [
            {
              relation: 'components',
            }
          ],
        }
      };
      return this.pageApi.findById(params).pipe(
        map((page) => new ContentEditorActions.GetPageByIdAndReplaceSuccessful({ page })
        ),
        catchError((error) => [
          new ContentEditorActions.ContentEditorError({
            errorOn: 'GetPageByIdAndReplace',
            error: error
          })
        ])
      );
    })
  ));

  scrollToElementBySelector(selector: string) {
    const element = document.querySelector(selector);

    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'nearest'
      });
    }
  }
}
